import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { fetchQuery, graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { fireErrorAnalytics } from '@atlassian/error-handling';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { CoverPictureIdPropertyKey, propertyToId } from '../../../media-components';
import {
	type useFetchPageCoverImageQuery,
	type useFetchPageCoverImageQuery$data,
} from './__generated__/useFetchPageCoverImageQuery.graphql';
import { messages } from './messages';

const extractId = (data?: useFetchPageCoverImageQuery$data) => {
	const isLivePage = data?.content?.nodes?.[0]?.subType === 'live';
	let imageProperty;
	if (isLivePage) {
		imageProperty = data?.content?.nodes?.[0]?.properties?.nodes?.find(
			(image) => image?.key === CoverPictureIdPropertyKey.DRAFT,
		);
	} else {
		imageProperty = data?.content?.nodes?.[0]?.properties?.nodes?.find(
			(image) => image?.key === CoverPictureIdPropertyKey.PUBLISHED,
		);
		if (!imageProperty) {
			imageProperty = data?.content?.nodes?.[0]?.properties?.nodes?.find(
				(image) => image?.key === CoverPictureIdPropertyKey.DRAFT,
			);
		}
	}
	return propertyToId(imageProperty)?.value.id;
};

export function useFetchCoverImage(
	analyticsSource: string,
): (confluencePageId: string | undefined, image?: { url?: string }) => Promise<string | undefined> {
	const { showFlag } = useFlags();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const environment = useRelayEnvironmentProvider().getEnvironment({});

	return useCallback(
		async (confluencePageId: string | undefined, image?: { url?: string }) => {
			if (!confluencePageId) {
				return image?.url;
			}

			let coverImageUrl: string | undefined;

			try {
				const data = await fetchQuery<useFetchPageCoverImageQuery>(
					environment,
					graphql`
						query useFetchPageCoverImageQuery($id: ID!) {
							content(id: $id) {
								nodes {
									id
									subType
									properties(keys: ["cover-picture-id-published", "cover-picture-id-draft"]) {
										nodes {
											id
											key
											value
										}
									}
								}
							}
						}
					`,
					{ id: confluencePageId ?? '' },
				).toPromise();
				coverImageUrl = extractId(data);

				if (coverImageUrl) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'selected',
							actionSubject: 'image',
							source: analyticsSource,
							attributes: {
								imageSource: 'imageFoundOnPage',
							},
						},
					}).fire();
				}
			} catch (error) {
				void showFlag({
					id: 'fetch-cover-image-error',
					title: formatMessage(messages.fetchErrorTitle),
					icon: <StatusErrorIcon label="" size="medium" color={token('color.icon.danger')} />,
					description: formatMessage(messages.fetchErrorDescription),
				});

				fireErrorAnalytics({
					error: error instanceof Error ? error : new Error('Failed to fetch page cover image'),
					meta: {
						id: 'useFetchPageCoverImage',
						packageName: '@atlassian/company-hub-common',
					},
				});
			}

			return coverImageUrl || image?.url;
		},

		[analyticsSource, createAnalyticsEvent, showFlag, formatMessage, environment],
	);
}
